import { useCallback, useRef, useState } from 'react'
import { fetchJwt } from '../../utils'
import { ChatMessage, ChatResponse, UserKnowledge } from './types'

const AI_CHAT_STREAM_URL = process.env.REACT_APP_SERVERLESS_API_URL
  ? `${process.env.REACT_APP_SERVERLESS_API_URL}/ai-chat-stream`
  : '/ai-chat-stream'

const AI_CHAT_URL = process.env.REACT_APP_SERVERLESS_API_URL
  ? `${process.env.REACT_APP_SERVERLESS_API_URL}/ai-chat`
  : '/ai-chat'

export type StreamPhase = 'idle' | 'thinking' | 'searching' | 'generating'

interface UseChatApiReturn {
  sendMessage: (
    message: string,
    conversationHistory: ChatMessage[],
    userKnowledge?: Partial<UserKnowledge>
  ) => Promise<ChatResponse>
  sendMessageStreaming: (
    message: string,
    conversationHistory: ChatMessage[],
    onDelta: (text: string) => void,
    onPhase?: (phase: StreamPhase) => void,
    userKnowledge?: Partial<UserKnowledge>
  ) => Promise<ChatResponse>
  isLoading: boolean
  phase: StreamPhase
  error: string | null
  clearError: () => void
  abort: () => void
}

// Parse SSE text buffer into events
function parseSSE(buffer: string): {
  events: Array<{ event: string; data: string }>
  remaining: string
} {
  const events: Array<{ event: string; data: string }> = []
  const lines = buffer.split('\n')
  let currentEvent = ''
  let currentData = ''
  let remaining = ''

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    if (line.startsWith('event: ')) {
      currentEvent = line.slice(7)
    } else if (line.startsWith('data: ')) {
      currentData = line.slice(6)
    } else if (line === '') {
      if (currentEvent && currentData) {
        events.push({ event: currentEvent, data: currentData })
        currentEvent = ''
        currentData = ''
      }
    } else {
      // Incomplete data — keep as remaining buffer
      remaining = lines.slice(i).join('\n')
      break
    }
  }

  // If we have an incomplete event at the end
  if (currentEvent || currentData) {
    remaining =
      (currentEvent ? `event: ${currentEvent}\n` : '') +
      (currentData ? `data: ${currentData}\n` : '') +
      remaining
  }

  return { events, remaining }
}

export const useChatApi = (): UseChatApiReturn => {
  const [isLoading, setIsLoading] = useState(false)
  const [phase, setPhase] = useState<StreamPhase>('idle')
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const abort = useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    setIsLoading(false)
    setPhase('idle')
  }, [])

  // Non-streaming fallback
  const sendMessage = useCallback(
    async (
      message: string,
      conversationHistory: ChatMessage[],
      userKnowledge?: Partial<UserKnowledge>
    ): Promise<ChatResponse> => {
      setIsLoading(true)
      setError(null)

      try {
        const token = fetchJwt()
        const response = await fetch(AI_CHAT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            message,
            conversationHistory: conversationHistory.slice(-20),
            userKnowledge
          })
        })

        if (response.status === 429) {
          const data = await response.json()
          throw new Error(data.error || 'Rate limit exceeded. Please try again later.')
        }

        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          throw new Error(data.error || 'Failed to get a response. Please try again.')
        }

        return await response.json()
      } catch (err: any) {
        const msg = err.message || 'Something went wrong. Please try again.'
        setError(msg)
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  // Streaming version
  const sendMessageStreaming = useCallback(
    async (
      message: string,
      conversationHistory: ChatMessage[],
      onDelta: (text: string) => void,
      onPhase?: (phase: StreamPhase) => void,
      userKnowledge?: Partial<UserKnowledge>
    ): Promise<ChatResponse> => {
      setIsLoading(true)
      setPhase('thinking')
      setError(null)
      onPhase?.('thinking')

      const controller = new AbortController()
      abortControllerRef.current = controller

      try {
        const token = fetchJwt()
        const response = await fetch(AI_CHAT_STREAM_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            message,
            conversationHistory: conversationHistory.slice(-20),
            userKnowledge
          }),
          signal: controller.signal
        })

        if (!response.ok) {
          // Try parsing as SSE error first, then JSON
          const text = await response.text()
          const { events } = parseSSE(text)
          const errorEvt = events.find((e) => e.event === 'error')
          if (errorEvt) {
            const d = JSON.parse(errorEvt.data)
            throw new Error(d.error || 'Request failed')
          }
          throw new Error('Failed to get a response. Please try again.')
        }

        // Read the full response body (arrives at once locally, chunked in production)
        const text = await response.text()
        const { events: allEvents } = parseSSE(text)

        let finalResult: ChatResponse | null = null
        const deltaEvents: string[] = []

        // First pass: collect all events, handle status/error/done immediately
        for (const evt of allEvents) {
          try {
            const data = JSON.parse(evt.data)
            switch (evt.event) {
              case 'delta':
                deltaEvents.push(data.text || '')
                break
              case 'status':
                if (data.phase) {
                  setPhase(data.phase)
                  onPhase?.(data.phase)
                }
                break
              case 'done':
                finalResult = data as ChatResponse
                break
              case 'error':
                throw new Error(data.error || 'Stream error')
            }
          } catch (parseErr: any) {
            if (parseErr.message === 'Stream error' || parseErr.message?.includes('Stream')) {
              throw parseErr
            }
            // JSON parse error — skip
          }
        }

        // Progressive rendering: reveal delta chunks with small delays
        if (deltaEvents.length > 0) {
          setPhase('generating')
          onPhase?.('generating')
          for (let i = 0; i < deltaEvents.length; i++) {
            if (controller.signal.aborted) break
            onDelta(deltaEvents[i])
            // Small delay between chunks for typing effect
            if (i < deltaEvents.length - 1) {
              await new Promise((r) => setTimeout(r, 5))
            }
          }
        }

        if (!finalResult) {
          throw new Error('Stream ended without a final response')
        }

        return finalResult
      } catch (err: any) {
        if (err.name === 'AbortError') {
          throw new Error('Request was cancelled')
        }
        const msg = err.message || 'Something went wrong. Please try again.'
        setError(msg)
        throw err
      } finally {
        setIsLoading(false)
        setPhase('idle')
        abortControllerRef.current = null
      }
    },
    []
  )

  const clearError = useCallback(() => setError(null), [])

  return { sendMessage, sendMessageStreaming, isLoading, phase, error, clearError, abort }
}
